/**
 * Shared Calendar Utilities
 *
 * This module contains all common logic for Nepali Calendar components.
 * It provides a single source of truth for date handling, month calculations,
 * and BS/AD conversions.
 */

import type { CalendarData } from "./api";
import { adToBS, bsToAD, getCurrentBSDate } from "./dateConverter";

// ============================================================================
// CONSTANTS
// ============================================================================

export const BS_MONTHS = [
  "बैशाख",
  "जेठ",
  "असार",
  "श्रावण",
  "भदौ",
  "आश्विन",
  "कार्तिक",
  "मंसिर",
  "पुष",
  "माघ",
  "फागुन",
  "चैत्र",
];

export const AD_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const NEPALI_DIGITS = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];

export const WEEKDAYS_BS = [
  "आइत",
  "सोम",
  "मंगल",
  "बुध",
  "बिही",
  "शुक्र",
  "शनि",
];
export const WEEKDAYS_AD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Supported date ranges
export const BS_YEAR_MIN = 1978;
export const BS_YEAR_MAX = 2099;
export const AD_YEAR_MIN = 1921;
export const AD_YEAR_MAX = 2032;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface BSDate {
  bs_y: number;
  bs_m: number;
  bs_d: number;
}

export interface ADDate {
  ad_y: number;
  ad_m: number;
  ad_d: number;
}

export interface DatePair {
  bs: BSDate;
  ad: ADDate;
  bsString: string;
  adString: string;
}

export interface MonthInfo {
  bsYear: number;
  bsMonth: number;
  adYear: number;
  adMonth: number;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert number to Nepali digits
 */
export const toNepaliNumber = (num: number): string => {
  return num
    .toString()
    .replace(/[0-9]/g, (match) => NEPALI_DIGITS[parseInt(match)]);
};

/**
 * Generate cache key for BS month data
 */
export const getMonthKey = (bsYear: number, bsMonth: number): string => {
  return `bs${bsYear}-${bsMonth}`;
};

/**
 * Format date as string (YYYY-MM-DD)
 */
export const formatDateString = (
  year: number,
  month: number,
  day: number
): string => {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
};

/**
 * Parse date string (YYYY-MM-DD) to components
 */
export const parseDateString = (
  dateString: string
): { year: number; month: number; day: number } | null => {
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  return { year, month, day };
};

/**
 * Normalize date string to YYYY-MM-DD format
 * Accepts both ISO datetime (YYYY-MM-DDThh:mm:ss.sssZ or YYYY-MM-DDThh:mm:ss.sss+HH:MM) and simple date (YYYY-MM-DD)
 * Returns null if invalid
 */
export const normalizeDateString = (
  dateString: string | null | undefined
): string | null => {
  if (!dateString || typeof dateString !== "string") return null;

  const trimmed = dateString.trim();
  if (!trimmed) return null;

  // Try to extract YYYY-MM-DD from the beginning
  // This regex matches: 2025-10-21, 2025-10-21T00:00:00, 2025-10-21T00:00:00.000Z, 2025-10-21T00:00:00.000+05:45
  const match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  // Basic validation
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
};

/**
 * Validate AD date is within supported range
 */
export const isValidADDate = (
  year: number,
  month: number,
  day: number
): boolean => {
  return (
    year >= AD_YEAR_MIN &&
    year <= AD_YEAR_MAX &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= 31
  );
};

/**
 * Validate BS date is within supported range
 */
export const isValidBSDate = (
  year: number,
  month: number,
  day: number
): boolean => {
  return (
    year >= BS_YEAR_MIN &&
    year <= BS_YEAR_MAX &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= 32
  );
};

// ============================================================================
// DATE CONVERSION FUNCTIONS
// ============================================================================

/**
 * Convert AD date string to both BS and AD date objects
 * Returns null if conversion fails
 */
export const convertADToDatePair = (adDateString: string): DatePair | null => {
  const parsed = parseDateString(adDateString);
  if (!parsed) return null;

  const { year, month, day } = parsed;
  if (!isValidADDate(year, month, day)) return null;

  const bsDate = adToBS(year, month, day);
  if (!bsDate) return null;

  return {
    bs: { bs_y: bsDate.bs_y, bs_m: bsDate.bs_m, bs_d: bsDate.bs_d },
    ad: { ad_y: year, ad_m: month, ad_d: day },
    bsString: formatDateString(bsDate.bs_y, bsDate.bs_m, bsDate.bs_d),
    adString: adDateString,
  };
};

/**
 * Convert BS date string to both BS and AD date objects
 * Returns null if conversion fails
 */
export const convertBSToDatePair = (bsDateString: string): DatePair | null => {
  const parsed = parseDateString(bsDateString);
  if (!parsed) return null;

  const { year, month, day } = parsed;
  if (!isValidBSDate(year, month, day)) return null;

  const adDate = bsToAD(year, month, day);
  if (!adDate) return null;

  return {
    bs: { bs_y: year, bs_m: month, bs_d: day },
    ad: { ad_y: adDate.ad_y, ad_m: adDate.ad_m, ad_d: adDate.ad_d },
    bsString: bsDateString,
    adString: formatDateString(adDate.ad_y, adDate.ad_m, adDate.ad_d),
  };
};

/**
 * Get today's date as DatePair
 */
export const getTodayDatePair = (): DatePair | null => {
  const currentBS = getCurrentBSDate();
  if (!currentBS) return null;

  const adDate = bsToAD(currentBS.bs_y, currentBS.bs_m, currentBS.bs_d);
  if (!adDate) return null;

  return {
    bs: { bs_y: currentBS.bs_y, bs_m: currentBS.bs_m, bs_d: currentBS.bs_d },
    ad: { ad_y: adDate.ad_y, ad_m: adDate.ad_m, ad_d: adDate.ad_d },
    bsString: formatDateString(currentBS.bs_y, currentBS.bs_m, currentBS.bs_d),
    adString: formatDateString(adDate.ad_y, adDate.ad_m, adDate.ad_d),
  };
};

// ============================================================================
// MONTH CALCULATION FUNCTIONS
// ============================================================================

/**
 * Get the BS month that contains a specific AD date
 * Uses day 15 as anchor for month determination
 */
export const getBSMonthForADMonth = (
  adYear: number,
  adMonth: number
): { bsYear: number; bsMonth: number } | null => {
  const bsDate = adToBS(adYear, adMonth, 15);
  if (!bsDate) return null;
  return { bsYear: bsDate.bs_y, bsMonth: bsDate.bs_m };
};

/**
 * Get the primary AD month for a BS month
 * Uses day 15 as anchor for month determination
 */
export const getADMonthForBSMonth = (
  bsYear: number,
  bsMonth: number
): { adYear: number; adMonth: number } | null => {
  const adDate = bsToAD(bsYear, bsMonth, 15);
  if (!adDate) return null;
  return { adYear: adDate.ad_y, adMonth: adDate.ad_m };
};

/**
 * Find which BS month contains a specific date
 * Searches the calendar data to find the exact month
 */
export const findBSMonthContainingDate = (
  datePair: DatePair,
  calendarData: Record<string, CalendarData>
): { bsYear: number; bsMonth: number } | null => {
  // First, check the converted BS month
  const key = getMonthKey(datePair.bs.bs_y, datePair.bs.bs_m);
  const data = calendarData[key];

  if (data?.days) {
    const found = data.days.find(
      (d) => d.bs.date === datePair.bsString || d.ad?.date === datePair.adString
    );
    if (found) {
      return { bsYear: datePair.bs.bs_y, bsMonth: datePair.bs.bs_m };
    }
  }

  // Search adjacent months if not found in expected month
  const searchMonths = [
    { year: datePair.bs.bs_y, month: datePair.bs.bs_m - 1 },
    { year: datePair.bs.bs_y, month: datePair.bs.bs_m + 1 },
  ].map(({ year, month }) => {
    if (month < 1) return { year: year - 1, month: 12 };
    if (month > 12) return { year: year + 1, month: 1 };
    return { year, month };
  });

  for (const { year, month } of searchMonths) {
    const searchKey = getMonthKey(year, month);
    const searchData = calendarData[searchKey];
    if (searchData?.days) {
      const found = searchData.days.find(
        (d) =>
          d.bs.date === datePair.bsString || d.ad?.date === datePair.adString
      );
      if (found) {
        return { bsYear: year, bsMonth: month };
      }
    }
  }

  // Fallback to converted BS month
  return { bsYear: datePair.bs.bs_y, bsMonth: datePair.bs.bs_m };
};

/**
 * Calculate previous month
 */
export const getPrevMonth = (
  year: number,
  month: number
): { year: number; month: number } => {
  if (month === 1) {
    return { year: year - 1, month: 12 };
  }
  return { year, month: month - 1 };
};

/**
 * Calculate next month
 */
export const getNextMonth = (
  year: number,
  month: number
): { year: number; month: number } => {
  if (month === 12) {
    return { year: year + 1, month: 1 };
  }
  return { year, month: month + 1 };
};

/**
 * Get AD month range string for a BS month (for display)
 */
export const getADMonthRangeForBSMonth = (
  bsYear: number,
  bsMonth: number,
  calendarData: Record<string, CalendarData>
): string | null => {
  const key = getMonthKey(bsYear, bsMonth);
  const data = calendarData[key];

  if (!data?.days || data.days.length === 0) return null;

  let firstAD: { month: number; year: number } | null = null;
  let lastAD: { month: number; year: number } | null = null;

  for (const day of data.days) {
    if (day.ad?.date) {
      const parsed = parseDateString(day.ad.date);
      if (parsed) {
        if (!firstAD) {
          firstAD = { month: parsed.month, year: parsed.year };
        }
        lastAD = { month: parsed.month, year: parsed.year };
      }
    }
  }

  if (!firstAD || !lastAD) return null;

  if (firstAD.month === lastAD.month && firstAD.year === lastAD.year) {
    return `(${AD_MONTHS[firstAD.month - 1]}, ${firstAD.year})`;
  }

  const yearDisplay =
    firstAD.year === lastAD.year
      ? `${firstAD.year}`
      : `${firstAD.year}-${lastAD.year}`;

  return `(${AD_MONTHS[firstAD.month - 1]}/${
    AD_MONTHS[lastAD.month - 1]
  }, ${yearDisplay})`;
};

// ============================================================================
// HOLIDAY UTILITIES
// ============================================================================

/**
 * Normalize holiday weekdays input to a Set of day indices
 */
export const normalizeHolidayWeekdays = (
  input: string | string[]
): Set<number> => {
  const toArray = Array.isArray(input) ? input : [input];
  const dayMap: Record<string, number> = {
    sunday: 0,
    sun: 0,
    monday: 1,
    mon: 1,
    tuesday: 2,
    tue: 2,
    wednesday: 3,
    wed: 3,
    thursday: 4,
    thu: 4,
    friday: 5,
    fri: 5,
    saturday: 6,
    sat: 6,
  };

  const set = new Set<number>();
  for (const item of toArray) {
    const key = String(item).trim().toLowerCase();
    if (dayMap[key] !== undefined) {
      set.add(dayMap[key]);
    }
  }
  return set;
};

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Get display month and year based on calendar mode
 */
export const getDisplayMonthYear = (
  mode: "bs" | "ad",
  bsYear: number,
  bsMonth: number,
  primaryADYear: number | null,
  primaryADMonth: number | null,
  calendarData: Record<string, CalendarData>
): {
  month: string;
  year: string;
  monthNum: number;
  yearNum: number;
} => {
  if (mode === "bs") {
    return {
      month: BS_MONTHS[bsMonth - 1],
      year: toNepaliNumber(bsYear),
      monthNum: bsMonth,
      yearNum: bsYear,
    };
  }

  // AD mode
  // Priority 1: Use primary AD month/year if set
  if (primaryADYear !== null && primaryADMonth !== null) {
    return {
      month: AD_MONTHS[primaryADMonth - 1],
      year: primaryADYear.toString(),
      monthNum: primaryADMonth,
      yearNum: primaryADYear,
    };
  }

  // Priority 2: Calculate from BS month data
  const key = getMonthKey(bsYear, bsMonth);
  const data = calendarData[key];

  if (data?.days && data.days.length > 0) {
    // Find majority AD month
    const monthCounts: Record<string, number> = {};
    for (const day of data.days) {
      if (day.ad?.date) {
        const parsed = parseDateString(day.ad.date);
        if (parsed) {
          const countKey = `${parsed.year}-${parsed.month}`;
          monthCounts[countKey] = (monthCounts[countKey] || 0) + 1;
        }
      }
    }

    let maxCount = 0;
    let majorityKey = "";
    for (const [k, count] of Object.entries(monthCounts)) {
      if (count > maxCount) {
        maxCount = count;
        majorityKey = k;
      }
    }

    if (majorityKey) {
      const [y, m] = majorityKey.split("-").map(Number);
      return {
        month: AD_MONTHS[m - 1],
        year: y.toString(),
        monthNum: m,
        yearNum: y,
      };
    }
  }

  // Fallback: Convert BS to AD
  const adInfo = getADMonthForBSMonth(bsYear, bsMonth);
  if (adInfo) {
    return {
      month: AD_MONTHS[adInfo.adMonth - 1],
      year: adInfo.adYear.toString(),
      monthNum: adInfo.adMonth,
      yearNum: adInfo.adYear,
    };
  }

  // Final fallback: Use current date
  const today = new Date();
  return {
    month: AD_MONTHS[today.getMonth()],
    year: today.getFullYear().toString(),
    monthNum: today.getMonth() + 1,
    yearNum: today.getFullYear(),
  };
};

/**
 * Get dropdown display values based on calendar mode
 */
export const getDropdownDisplayValues = (
  mode: "bs" | "ad",
  bsYear: number,
  bsMonth: number,
  primaryADYear: number | null,
  primaryADMonth: number | null
): { selectedMonth: number; selectedYear: number } => {
  if (mode === "bs") {
    return { selectedMonth: bsMonth, selectedYear: bsYear };
  }

  // AD mode
  if (primaryADYear !== null && primaryADMonth !== null) {
    return { selectedMonth: primaryADMonth, selectedYear: primaryADYear };
  }

  // Fallback: convert BS to AD
  const adInfo = getADMonthForBSMonth(bsYear, bsMonth);
  if (adInfo) {
    return { selectedMonth: adInfo.adMonth, selectedYear: adInfo.adYear };
  }

  return { selectedMonth: bsMonth, selectedYear: bsYear };
};
